import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Check, Eye, EyeOff, Lock } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import { useResetPassword } from '../api/useForgotPassword'

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/\d/, 'Password must contain at least one number'),
    confirmPassword: z.string(),
  })
  .refine(d => d.password === d.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords don\'t match',
  })

type Values = z.infer<typeof schema>

export function ResetPasswordForm() {
  const token = new URLSearchParams(window.location.search).get('token') || undefined
  const emailFromQuery = new URLSearchParams(window.location.search).get('email') || undefined
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  })
  const mutation = useResetPassword()

  const maskEmail = (email?: string) => {
    if (!email)
      return undefined
    const [name, domain] = email.split('@')
    if (!domain)
      return email
    const visible = name.slice(0, 2)
    const masked = '*'.repeat(Math.max(3, name.length - 2))
    return `${visible}${masked}@${domain}`
  }

  const watchPassword = form.watch('password')
  const passwordRequirements = [
    { label: 'At least 8 characters', test: (pwd: string) => pwd.length >= 8 },
    { label: 'One uppercase letter', test: (pwd: string) => /[A-Z]/.test(pwd) },
    { label: 'One lowercase letter', test: (pwd: string) => /[a-z]/.test(pwd) },
    { label: 'One number', test: (pwd: string) => /\d/.test(pwd) },
  ]
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const onSubmit = async (values: Values) => {
    if (!token) {
      toast.error('Missing reset token')
      return
    }
    try {
      const res = await mutation.mutateAsync({ token, password: values.password, email: emailFromQuery || '' })
      const message = (res.data as any)?.message || 'Password has been reset. You can now sign in.'
      toast.success(message)
      // navigate to login
      window.location.href = '/login'
    }
    catch (err) {
      const message = (err as any)?.response?.data?.message || 'Unable to reset password'
      toast.error(message)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl font-semibold">Reset password</CardTitle>
        <CardDescription>
          {emailFromQuery
            ? (
              <span>
                Enter a new password for
                {' '}
                <span className="font-medium">{maskEmail(emailFromQuery)}</span>
                .
              </span>
            )
            : (
              'Enter a new password for your account.'
            )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>New password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        className="pl-10 pr-10 h-11"
                        placeholder="Enter new password"
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword(s => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword
                          ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          )
                          : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                      </Button>
                    </div>
                  </FormControl>
                  {watchPassword && (
                    <div className="mt-2 space-y-1">
                      {passwordRequirements.map(req => (
                        <div key={req.label} className="flex items-center space-x-2 text-xs">
                          <div
                            className={`w-3 h-3 rounded-full flex items-center justify-center ${req.test(watchPassword)
                              ? 'bg-green-500'
                              : 'bg-gray-300'
                            }`}
                          >
                            {req.test(watchPassword) && <Check className="w-2 h-2 text-white" />}
                          </div>
                          <span className={req.test(watchPassword) ? 'text-green-600' : 'text-muted-foreground'}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="confirmPassword"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Confirm password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        className="pl-10 pr-10 h-11"
                        placeholder="Re-enter new password"
                        autoComplete="new-password"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword(s => !s)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword
                          ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          )
                          : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button className="w-full h-11" disabled={mutation.isPending}>
              {mutation.isPending
                ? 'Saving…'
                : (
                  <span className="inline-flex items-center gap-2">
                    Reset password
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
